import React, { useState, useEffect } from 'react';
import farmers from '../assets/4072378.jpg';
import { 
  CheckCircle, 
  Users, 
  Leaf, 
  Award, 
  TrendingUp, 
  RefreshCw, 
  Zap, 
  ShoppingCart,
  UserCheck,
  Truck,
  BarChart3,
  Handshake,
  Target,
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  Globe
} from 'lucide-react';

const JVPFarms = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    alert(`Thank you for your message, ${formData.name}! We'll get back to you soon.`);
    setFormData({ name: '', email: '', message: '' });
  };

  const features = [
    {
      icon: <Leaf className="w-8 h-8 text-green-600" />,
      title: "Regenerative Agriculture",
      description: "We implement cutting-edge regenerative farming techniques that restore soil health, increase biodiversity, and sequester carbon. Our closed-loop systems minimize waste while maximizing yield through natural processes."
    },
    {
      icon: <Award className="w-8 h-8 text-blue-600" />,
      title: "Carbon-Neutral Certification",
      description: "All our produce comes with carbon-neutral certification. We've invested in renewable energy, carbon offsets, and sustainable packaging to ensure our environmental impact is net positive."
    },
      {
      icon: <RefreshCw className="w-8 h-8 text-green-600" />,
      title: "Circular Economy Model",
      description: "Our zero-waste philosophy means we repurpose 100% of byproducts. From composting to biofuel production, we've developed innovative solutions that turn waste into valuable resources."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Smart Farming Technology",
      description: "Using IoT sensors, AI-driven analytics, and precision agriculture, we optimize water usage, reduce chemical inputs, and predict yields with 95% accuracy while minimizing environmental impact."
    },
    {
      icon: <Zap className="w-8 h-8 text-green-600" />,
      title: "Biodiversity Conservation",
      description: "We maintain 20% of our land as natural habitat, supporting native species and pollinators. Our integrated pest management eliminates the need for harmful pesticides while maintaining crop health."
    }
  ];

  const services = [
    {
      icon: <Leaf className="w-12 h-12 text-white" />,
      title: "Regenerative Farming",
      description: "Our soil-first approach rebuilds organic matter and restores degraded land. We use cover cropping, no-till methods, and rotational grazing to create resilient ecosystems that produce nutrient-dense food.",
      bgColor: "bg-green-600"
    },
    {
      icon: <UserCheck className="w-12 h-12 text-white" />,
      title: "Fair Trade Workforce",
      description: "We exceed fair trade standards with living wages, profit sharing, and comprehensive benefits. Our worker-owned cooperative model ensures everyone shares in our success.",
      bgColor: "bg-teal-600"
    },
    {
      icon: <Truck className="w-12 h-12 text-white" />,
      title: "Hyper-Local Distribution",
      description: "Our blockchain-tracked supply chain guarantees freshness and transparency. 90% of our produce reaches customers within 24 hours of harvest through our electric vehicle fleet.",
      bgColor: "bg-emerald-600"
    },
    {
      icon: <ShoppingCart className="w-12 h-12 text-white" />,
      title: "Climate-Smart Solutions",
      description: "We help businesses calculate and offset their foodprint. Our carbon accounting platform provides actionable insights to reduce supply chain emissions.",
      bgColor: "bg-green-700"
    },
    {
      icon: <Handshake className="w-12 h-12 text-white" />,
      title: "Agricultural Carbon Credits",
      description: "Partner with us to generate verified carbon credits through our regenerative practices. We offer turnkey programs that monetize sustainability while meeting ESG goals.",
      bgColor: "bg-teal-700"
    },
    {
      icon: <Target className="w-12 h-12 text-white" />,
      title: "Sustainable Transition Consulting",
      description: "Our team of agronomists and data scientists help conventional farms transition to regenerative models. We provide ROI analysis, implementation roadmaps, and certification support.",
      bgColor: "bg-emerald-700"
    }
  ];

  const sustainabilityStats = [
    { value: "100%", label: "Renewable Energy Powered" },
    { value: "250+", label: "Native Species Supported" },
    { value: "Zero", label: "Chemical Pesticides Used" },
    { value: "78%", label: "Water Usage Reduction" },
    { value: "1,200+", label: "Tons Carbon Sequestered" },
    { value: "100%", label: "Waste Repurposed" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg font-bold text-lg shadow-md">
                Sustain Enviro
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`font-medium transition-colors ${
                scrollY > 50 ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}>HOME</a>
              <a href="#about" className={`font-medium transition-colors ${
                scrollY > 50 ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}>ABOUT</a>
              <a href="#sustainability" className={`font-medium transition-colors ${
                scrollY > 50 ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}>SUSTAINABILITY</a>
              <a href="#services" className={`font-medium transition-colors ${
                scrollY > 50 ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}>SERVICES</a>
              <a href="#contact" className={`font-medium transition-colors ${
                scrollY > 50 ? 'text-gray-700 hover:text-green-600' : 'text-white hover:text-green-300'
              }`}>CONTACT</a>
              {/* <button className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2 rounded-full hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg font-medium">
                Get Started
              </button> */}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-md ${scrollY > 50 ? 'text-gray-700' : 'text-white'}`}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <a href="#home" className="block px-3 py-2 text-gray-700 hover:text-green-600">HOME</a>
                <a href="#about" className="block px-3 py-2 text-gray-700 hover:text-green-600">ABOUT</a>
                <a href="#sustainability" className="block px-3 py-2 text-gray-700 hover:text-green-600">SUSTAINABILITY</a>
                <a href="#services" className="block px-3 py-2 text-gray-700 hover:text-green-600">SERVICES</a>
                <a href="#contact" className="block px-3 py-2 text-gray-700 hover:text-green-600">CONTACT</a>
                <button className="w-full mt-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2 rounded-full hover:from-green-700 hover:to-emerald-700 transition-all font-medium">
                  Get Started
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-90" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 to-emerald-800/80"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Regenerating Agriculture <br />for a <span className="text-green-300">Sustainable Future</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed max-w-3xl mx-auto">
            At Sustain Enviro, we're pioneering climate-positive farming that restores ecosystems while delivering premium organic produce through ethical, transparent practices.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            {/* <button className="bg-white text-green-700 px-8 py-3 rounded-full hover:bg-gray-100 transition-all duration-300 font-medium text-lg shadow-lg">
              Explore Our Farm
            </button> */}
          <a href="#contact">
  <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full hover:bg-white hover:text-green-600 transition-all duration-300 font-medium text-lg">
    Contact Us
  </button>
</a>
          </div>
        </div>
        
        {/* Animated floating leaves */}
        <div className="absolute bottom-10 left-10 animate-float">
          <Leaf className="w-8 h-8 text-green-300 opacity-70" />
        </div>
        <div className="absolute top-20 right-20 animate-float delay-1000">
          <Leaf className="w-6 h-6 text-green-200 opacity-70" />
        </div>
        <div className="absolute top-1/3 left-1/4 animate-float delay-2000">
          <Leaf className="w-5 h-5 text-green-100 opacity-70" />
        </div>
      </section>

      {/* Sustainability Impact Section */}
      <section id="sustainability" className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              OUR IMPACT
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Measuring What Matters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We quantify our environmental and social impact with the same rigor we apply to our crop yields.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {sustainabilityStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="text-3xl font-bold text-green-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={farmers}
                  alt="Farm workers harvesting" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="text-gray-600">Regenerating Australian farmland</div>
              </div>
            </div>
            
            <div>
              <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                OUR STORY
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Farming in Harmony With Nature</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Founded by agricultural scientists and environmentalists, Sustain Enviro began with a simple question: 
                How can we grow food that actually improves the land? Our answer became Australia's first 
                certified regenerative organic farm, proving that agriculture can be a solution to climate change.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-2 rounded-full">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Worker-Owned Cooperative</h4>
                    <p className="text-gray-700 leading-relaxed">
                      We transitioned to a worker-owned model in 2018, ensuring all team members share in both 
                      decision-making and profits. This creates unparalleled commitment to quality and sustainability.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-2 rounded-full">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Global Recognition</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Our innovative practices have earned us the UN Sustainable Development Goals Award and 
                      certification as Australia's first Climate Positive farm by Carbon Neutral.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              WHY CHOOSE US
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Beyond Organic, Beyond Sustainable</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We don't just minimize harm—we actively regenerate ecosystems while producing the most nutrient-dense food available.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-200">
                <div className="mb-6 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section className="py-20 bg-gradient-to-r from-green-900 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-4">
                INNOVATION
              </span>
              <h2 className="text-4xl font-bold mb-6">Smart Farming Meets Ancient Wisdom</h2>
              <p className="text-lg mb-8 leading-relaxed opacity-90">
                We combine cutting-edge technology with time-tested ecological principles. Our farm is a living laboratory where 
                drones monitor plant health, AI optimizes irrigation, and blockchain tracks every carrot from seed to shelf—all 
                while following indigenous land management practices.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold mb-2">IoT</div>
                  <p className="opacity-80">200+ sensors monitoring soil and microclimate</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">AI</div>
                  <p className="opacity-80">Predictive analytics for disease prevention</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">Blockchain</div>
                  <p className="opacity-80">Full supply chain transparency</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">Solar</div>
                  <p className="opacity-80">100% renewable energy operation</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                  alt="Farm technology" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white text-green-900 p-4 rounded-xl shadow-lg">
                <div className="font-bold">Real-time Data Dashboard</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              WHAT WE OFFER
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Holistic Agricultural Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From premium produce to climate consultancy, we provide comprehensive services that align profit with planetary health.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className={`${service.bgColor} p-8 text-center relative h-48 flex flex-col items-center justify-center`}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4 z-10 group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white z-10">{service.title}</h3>
                </div>
                <div className="p-8">
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  {/* <button className="text-green-600 font-medium hover:text-green-800 transition-colors flex items-center">
                    Learn more
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              TRUSTED PARTNERS
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Partners Say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 italic mb-6">
                "Sustain Enviro has transformed our supply chain. Their regenerative produce not only tastes better but aligns perfectly with our sustainability commitments. The blockchain traceability gives our customers unparalleled transparency."
              </p>
              <div className="flex items-center">
                <img className="w-12 h-12 rounded-full mr-4" src="https://randomuser.me/api/portraits/women/43.jpg" alt="Sarah Johnson" />
                <div>
                  <div className="font-bold">Sarah Johnson</div>
                  <div className="text-gray-500 text-sm">CEO, Green Grocers Australia</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 italic mb-6">
                "As a chef, ingredient quality is everything. Sustain Enviro's produce consistently outperforms—the flavor complexity from their mineral-rich soil is unmatched. Their farm-to-kitchen in under 12 hours makes all the difference."
              </p>
              <div className="flex items-center">
                <img className="w-12 h-12 rounded-full mr-4" src="https://randomuser.me/api/portraits/men/32.jpg" alt="Michael Chen" />
                <div>
                  <div className="font-bold">Michael Chen</div>
                  <div className="text-gray-500 text-sm">Executive Chef, Terra Restaurant</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 italic mb-6">
                "Their carbon farming program helped us offset 30% of our emissions last year while supporting Australian agriculture. The verification process was rigorous but worth it for our ESG reporting."
              </p>
              <div className="flex items-center">
                <img className="w-12 h-12 rounded-full mr-4" src="https://randomuser.me/api/portraits/women/68.jpg" alt="Priya Patel" />
                <div>
                  <div className="font-bold">Priya Patel</div>
                  <div className="text-gray-500 text-sm">Sustainability Director, Global Corp</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                GET IN TOUCH
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Let's Grow Together</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you're interested in our produce, carbon programs, or regenerative transition services, 
                our team is ready to help you cultivate sustainable solutions.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Mail className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Email Us</h4>
                    <p className="text-gray-600">info@sustainenviroptyltd.com</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Call Us</h4>
                    <p className="text-gray-600">+61 403 947 374</p>
                  </div>
                </div>
                
                {/* <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Visit Us</h4>
                    <p className="text-gray-600">123 Regenerative Lane</p>
                    <p className="text-gray-600">Byron Bay, NSW 2481, Australia</p>
                  </div>
                </div> */}
                
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Hours</h4>
                    <p className="text-gray-600">Monday-Friday: 8am - 5pm</p>
                    <p className="text-gray-600">Saturday: 9am - 2pm</p>
                    <p className="text-gray-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-md font-medium flex items-center justify-center"
                >
                  Send Message
                  <Send className="w-5 h-5 ml-2" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

    

      {/* Footer */}
    <footer className="bg-gray-900 text-white py-12">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 md:grid-cols-4 items-start gap-y-8 md:gap-x-16">
      
      {/* Info Section (left side) */}
      <div className="md:col-span-3">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg font-bold text-lg inline-block mb-4">
          Sustain Enviro
        </div>
        <p className="text-gray-300 leading-relaxed mb-6">
          Sustain Enviro is committed to regenerating Australian agriculture through climate-positive farming practices, 
          ethical employment, and the integration of cutting-edge sustainable technologies. We strive to create a resilient 
          future by aligning ecological balance with economic prosperity.
        </p>
      </div>

      {/* Quick Links (right side) */}
      <div>
        <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
        <ul className="space-y-2">
          <li><a href="#home" className="text-gray-300 hover:text-green-400 transition-colors">Home</a></li>
          <li><a href="#about" className="text-gray-300 hover:text-green-400 transition-colors">About</a></li>
          <li><a href="#sustainability" className="text-gray-300 hover:text-green-400 transition-colors">Sustainability</a></li>
          <li><a href="#services" className="text-gray-300 hover:text-green-400 transition-colors">Services</a></li>
          <li><a href="#contact" className="text-gray-300 hover:text-green-400 transition-colors">Contact</a></li>
        </ul>
      </div>

    </div>

    {/* Footer Bottom */}
    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
      <p>
        &copy; {new Date().getFullYear()} Sustain Enviro. All rights reserved. Proudly regenerative in Australia.
      </p>
    </div>
  </div>
</footer>

    </div>
  );
};

export default JVPFarms;